<template>
  <div class="auth-page">
    <div class="auth-card">
      <div class="auth-header">
        <img src="@/assets/img/logo.png" alt="Logo" class="logo" />
        <h1>Willkommen zurück</h1>
        <p>Bitte melden Sie sich mit Ihren Zugangsdaten an</p>
      </div>
      
      <div class="auth-body">
        <form @submit.prevent="handleLogin">
          <div class="alert alert-danger" v-if="error">
            {{ error }}
          </div>
          
          <div class="form-group mb-3">
            <label for="email" class="form-label">E-Mail-Adresse</label>
            <input 
              type="email" 
              id="email"
              v-model="email"
              class="form-control" 
              required
              autofocus
              placeholder="name@beispiel.de"
            />
          </div>
          
          <div class="form-group mb-4">
            <div class="d-flex justify-content-between">
              <label for="password" class="form-label">Passwort</label>
              <a href="#" class="small text-primary">Passwort vergessen?</a>
            </div>
            <input 
              type="password" 
              id="password" 
              v-model="password"
              class="form-control" 
              required
              placeholder="Passwort eingeben"
            />
          </div>
          
          <div class="form-check mb-4">
            <input class="form-check-input" type="checkbox" id="remember" v-model="remember">
            <label class="form-check-label" for="remember">
              Angemeldet bleiben
            </label>
          </div>
          
          <button 
            type="submit" 
            class="btn btn-primary w-100 btn-lg" 
            :disabled="loading"
          >
            <span class="spinner-border spinner-border-sm me-2" v-if="loading"></span>
            <i class="fas fa-sign-in-alt me-2" v-else></i>
            Anmelden
          </button>
        </form>
      </div>
      <!--
      <div class="auth-footer">
        <p>Noch kein Konto? <router-link to="/register">Registrieren</router-link></p>
      </div>
      -->
    </div>
  </div>
</template>

<script>
import { defineComponent, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';
import { fetchCSRFToken } from '@/services/http';
import { useToast } from 'vue-toastification';

export default defineComponent({
  name: 'Login',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const toast = useToast();
    const authStore = useAuthStore();
    const companyStore = useCompanyStore();
    
    const email = ref('');
    const password = ref('');
    const remember = ref(false);
    const loading = ref(false);
    const error = ref(null);
    
    const handleLogin = async () => {
      try {
        loading.value = true;
        error.value = null;
        
        // Login-Anfrage senden
        const response = await authStore.login({
          email: email.value,
          password: password.value,
          remember: remember.value
        });
        
        toast.success('Erfolgreich angemeldet');
        
        // Nach Login zur richtigen Startseite weiterleiten
        const user = response.user;
        
        // Firma explizit initialisieren
        console.log('Lade Firmendaten nach Login...');
        await companyStore.initializeCompany();
        
        // Zur richtigen Startseite weiterleiten
        if (user.role === 'super_admin') {
          router.push('/admin/dashboard');
        } else {
          router.push('/company/dashboard');
        }
      } catch (err) {
        if (err.response) {
          
          // Bei 405 Method Not Allowed - falsche Route
          if (err.response.status === 405) {
            error.value = 'Technisches Problem mit der Anmeldefunktion. Das System verwendet möglicherweise eine falsche API-Route.';
          } else if (err.response.status === 422) {
            error.value = 'Ungültige Anmeldedaten. Bitte überprüfen Sie Ihre E-Mail und Passwort.';
          } else {
            error.value = err.response.data.message || 'Bei der Anmeldung ist ein Fehler aufgetreten.';
          }
        } else {
          error.value = 'Verbindungsproblem. Bitte prüfen Sie Ihre Internetverbindung.';
        }
        
        console.error('Login-Fehler:', err);
      } finally {
        loading.value = false;
      }
    };
    
    const initializeLoginPage = async () => {
      // CSRF-Token abrufen, aber nur einmal
      try {
        await fetchCSRFToken();
      } catch (e) {
        console.error('Fehler beim Abrufen des CSRF-Tokens:', e);
      }
    };
    
    // Bei Montage ausführen
    if (route.query.clean === 'true' || 
        route.query.expired === 'true' || 
        route.query.error === 'true') {
      console.log('Sauberer Seitenzustand nach Logout/Fehler');
      
      // Alle Cookies für diese Domain löschen
      document.cookie.split(';').forEach(cookie => {
        const [name] = cookie.trim().split('=');
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      });
      
      // LocalStorage vollständig bereinigen
      localStorage.clear();
      
      // URL-Parameter entfernen
      router.replace('/login');
    } else {
      // Normale Login-Initialisierung
      initializeLoginPage();
    }
    
    return {
      email,
      password,
      remember,
      loading,
      error,
      handleLogin
    };
  }
});
</script>

<style lang="scss" scoped>
.auth-page {
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.auth-card {
  width: 100%;
  max-width: 400px;
  margin: 1.5rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  position: relative;
  z-index: 1;
  
  .auth-header {
    padding: 2rem;
    text-align: center;
    border-bottom: 1px solid #eee;
    
    .logo {
      margin-bottom: 1.5rem;
    }
    
    h1 {
      font-size: 1.5rem;
      font-weight: 600;
      color: #333;
      margin-bottom: 0.5rem;
    }
    
    p {
      color: #666;
      font-size: 0.95rem;
      margin: 0;
    }
  }
  
  .auth-body {
    padding: 2rem;
    
    .form-group {
      margin-bottom: 1.5rem;
    }
    
    .form-label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 500;
      color: #444;
    }
    
    .form-control {
      width: 100%;
      height: 42px;
      padding: 0.5rem 0.75rem;
      border: 1.5px solid #ddd;
      border-radius: 6px;
      transition: border-color 0.2s;
      
      &:focus {
        outline: none;
        border-color: var(--bs-primary);
        box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb), 0.1);
      }
      
      &::placeholder {
        color: #aaa;
      }
    }
    
    .alert {
      margin-bottom: 1.5rem;
      padding: 0.75rem 1rem;
      border-radius: 6px;
      font-size: 0.9rem;
      
      &.alert-danger {
        background-color: #fff5f5;
        color: #dc3545;
        border: 1px solid #ffebeb;
      }
    }
    
    .form-check {
      margin: 1.5rem 0;
      
      .form-check-input {
        margin-top: 0.2rem;
      }
      
      .form-check-label {
        color: #666;
        font-size: 0.9rem;
      }
    }
    
    .btn-primary {
      width: 100%;
      height: 42px;
      font-weight: 500;
      transition: all 0.2s;
      
      &:not(:disabled):hover {
        transform: translateY(-1px);
      }
      
      &:disabled {
        opacity: 0.7;
      }
    }
  }
  
  .auth-footer {
    padding: 1.5rem;
    text-align: center;
    background-color: #f8f9fa;
    border-top: 1px solid #eee;
    
    p {
      margin: 0;
      font-size: 0.9rem;
      color: #666;
      
      a {
        color: var(--bs-primary);
        text-decoration: none;
        font-weight: 500;
        
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}

// Footer-Styles
:deep(.app-footer) {
  position: fixed;
  bottom: 0;
  left: 0px !important;
  width: 100%;
  background-color: transparent;
  padding: 1rem;
  text-align: center;
  z-index: 2;
  
  p, a {
    color: white;
    margin: 0;
    text-decoration: none;
    font-size: 0.9rem;
    opacity: 0.9;
    
    &:hover {
      opacity: 1;
    }
  }
}

.app-footer {
  left: 0px !important;
}

@media (max-width: 576px) {
  .auth-card {
    margin: 1rem;
  }
  
  :deep(.app-footer) {
    padding: 0.75rem;
    
    p, a {
      font-size: 0.8rem;
    }
  }
}
</style> 